'use client';

import React, { type CSSProperties, type MouseEventHandler, type ReactNode } from 'react';
import { useColors } from '../theme/ThemeProvider';
import { radius, space, motion } from '../theme/tokens';

export interface CardProps {
  children: ReactNode;
  onClick?: MouseEventHandler<HTMLDivElement>;
  /** Reduces default padding (e.g. for tight lists). */
  compact?: boolean;
  /** Escape hatch for one-off adjustments. */
  style?: CSSProperties;
}

/**
 * Elevated surface container.
 * Automatically themed. Uses shadow on light modes, subtle border on dark.
 */
export function Card({ children, onClick, compact = false, style }: CardProps) {
  const colors = useColors();
  const hasShadow = colors.shadow && colors.shadow !== 'none';
  const isDark = !hasShadow; // dark mode: keine farbigen Light-Shadows
  const interactive = !!onClick;

  // Dark-Polish: Verlauf-Oberfläche (oben minimal heller), weicher Schlagschatten
  // + feine helle Glanzkante (Spekular-Reflex) statt mattem Flat-Schwarz.
  const darkBackground = `linear-gradient(180deg, ${colors.surfaceAlt} 0%, ${colors.surface} 100%)`;
  const darkElevation = '0 10px 28px -12px rgba(0,0,0,0.55), inset 0 1px 0 rgba(255,255,255,0.05)';

  // Phase 8.4 — Press-Feedback nur für tappable Cards: dezente Skalierung
  // beim Drücken, gleicher Pattern wie Button. Touch + Maus.
  const press = (e: React.SyntheticEvent<HTMLDivElement>) => {
    (e.currentTarget as HTMLDivElement).style.transform = 'scale(0.985)';
  };
  const release = (e: React.SyntheticEvent<HTMLDivElement>) => {
    (e.currentTarget as HTMLDivElement).style.transform = 'scale(1)';
  };

  return (
    <div
      onClick={onClick}
      role={interactive ? 'button' : undefined}
      tabIndex={interactive ? 0 : undefined}
      onMouseDown={interactive ? press : undefined}
      onMouseUp={interactive ? release : undefined}
      onMouseLeave={interactive ? release : undefined}
      onTouchStart={interactive ? press : undefined}
      onTouchEnd={interactive ? release : undefined}
      style={{
        background: isDark ? darkBackground : colors.surface,
        borderRadius: radius.xl,
        padding: compact ? `${space[3]}px ${space[4]}px` : `${space[5]}px`,
        marginBottom: space[3],
        // Light: weicher Schatten. Dark: Verlauf + Elevation + Glanzkante.
        boxShadow: hasShadow ? colors.shadow : darkElevation,
        border: hasShadow ? 'none' : `0.5px solid ${colors.border}`,
        cursor: interactive ? 'pointer' : undefined,
        transition: `transform ${motion.duration.fast}ms ${motion.easing.out}, background ${motion.duration.base}ms, box-shadow ${motion.duration.base}ms, border-color ${motion.duration.base}ms`,
        ...style,
      }}
    >
      {children}
    </div>
  );
}
